import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Camera, Package } from 'lucide-react';
import { snapshotsApi } from '../services/api';
import type { Zone } from '../types';

export interface SnapshotThumbnailHandle {
  refresh: () => void;
}

interface CameraSnapshotThumbnailProps {
  cameraId: string;
  cameraName: string;
  isOnline?: boolean;
  zones?: Zone[];
  zoneCounts?: Map<string, number>;
  /** Auto-refresh interval in ms. When set, snapshot re-fetches periodically for a live feed effect. */
  refreshInterval?: number;
  /** Incrementing counter that triggers an immediate re-fetch when it changes. */
  refreshKey?: number;
}

const CameraSnapshotThumbnail = forwardRef<SnapshotThumbnailHandle, CameraSnapshotThumbnailProps>(function CameraSnapshotThumbnail({
  cameraId,
  cameraName,
  isOnline = false,
  zones = [],
  zoneCounts = new Map(),
  refreshInterval,
  refreshKey,
}, ref) {
  const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const prevUrlRef = useRef<string | null>(null);

  const fetchSnapshot = useCallback(() => {
    snapshotsApi
      .get(cameraId)
      .then((blob) => {
        if (prevUrlRef.current) {
          URL.revokeObjectURL(prevUrlRef.current);
        }
        const url = URL.createObjectURL(blob);
        prevUrlRef.current = url;
        setSnapshotUrl(url);
        setError(false);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [cameraId]);

  // Expose refresh to parent via ref
  useImperativeHandle(ref, () => ({ refresh: fetchSnapshot }), [fetchSnapshot]);

  // Fetch snapshot on mount + optionally auto-refresh at interval
  useEffect(() => {
    fetchSnapshot();

    let interval: ReturnType<typeof setInterval> | null = null;
    if (refreshInterval && refreshInterval > 0) {
      interval = setInterval(fetchSnapshot, refreshInterval);
    }

    return () => {
      if (interval) clearInterval(interval);
      if (prevUrlRef.current) {
        URL.revokeObjectURL(prevUrlRef.current);
        prevUrlRef.current = null;
      }
    };
  }, [fetchSnapshot, refreshInterval]);

  // Re-fetch when refreshKey changes (triggered by WebSocket snapshot_uploaded event)
  useEffect(() => {
    if (refreshKey !== undefined && refreshKey > 0) {
      fetchSnapshot();
    }
  }, [refreshKey, fetchSnapshot]);

  // Draw zone overlays on canvas - redraws automatically when zoneCounts changes (AJAX-style)
  useEffect(() => {
    if (!snapshotUrl || !canvasRef.current || !containerRef.current || zones.length === 0) return;

    const canvas = canvasRef.current;
    const container = containerRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size to match container
    const rect = container.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw each zone
    zones.forEach((zone) => {
      const count = zoneCounts.get(zone.id);
      const isLowStock = zone.low_stock_threshold !== null &&
                         count !== undefined &&
                         count < zone.low_stock_threshold;

      // Calculate pixel coordinates from normalized (0-1) values
      const x = zone.x * canvas.width;
      const y = zone.y * canvas.height;
      const width = zone.width * canvas.width;
      const height = zone.height * canvas.height;

      // Zone boundary color
      const color = isLowStock ? '#f97316' : '#3b82f6'; // orange-500 or blue-500

      // Draw dashed rectangle
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(x, y, width, height);

      // Draw label background
      const label = count !== undefined ? `${zone.name}: ${count}` : zone.name;
      ctx.font = 'bold 10px sans-serif';
      const textMetrics = ctx.measureText(label);
      const labelPadding = 4;
      const labelHeight = 14;
      const labelWidth = textMetrics.width + labelPadding * 2;

      // Position label at top of zone
      const labelX = x;
      const labelY = y - labelHeight - 2;

      // Draw label background
      ctx.fillStyle = isLowStock ? '#f97316' : '#3b82f6';
      ctx.setLineDash([]);
      ctx.fillRect(labelX, labelY, labelWidth, labelHeight);

      // Draw label text
      ctx.fillStyle = '#ffffff';
      ctx.fillText(label, labelX + labelPadding, labelY + 10);

      // Draw warning indicator for low stock
      if (isLowStock) {
        ctx.fillStyle = '#f97316';
        ctx.font = 'bold 12px sans-serif';
        ctx.fillText('⚠️', labelX + labelWidth + 4, labelY + 11);
      }
    });
  }, [snapshotUrl, zones, zoneCounts]);

  // Calculate summary stats
  const totalCount = Array.from(zoneCounts.values()).reduce((sum, c) => sum + c, 0);
  const lowStockCount = zones.filter((z) => {
    const count = zoneCounts.get(z.id);
    return z.low_stock_threshold !== null && count !== undefined && count < z.low_stock_threshold;
  }).length;

  const hasZones = zones.length > 0;
  const hasCounts = zoneCounts.size > 0;

  return (
    <div ref={containerRef} className="aspect-video bg-gray-100 dark:bg-slate-700 -mx-4 -mt-4 mb-4 relative overflow-hidden">
      {loading ? (
        <div className="w-full h-full flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gray-300 dark:border-slate-600 border-t-primary-500 rounded-full animate-spin" />
        </div>
      ) : error || !snapshotUrl ? (
        <div className="w-full h-full flex items-center justify-center bg-gray-100 dark:bg-slate-700">
          <Camera className="w-12 h-12 text-gray-300 dark:text-slate-500" />
        </div>
      ) : (
        <>
          <img
            src={snapshotUrl}
            alt={`${cameraName} snapshot`}
            className="w-full h-full object-cover"
          />
          {/* Canvas overlay for zone boundaries */}
          <canvas
            ref={canvasRef}
            className="absolute inset-0 pointer-events-none"
          />
        </>
      )}

      {/* Online status badge - top right */}
      <span
        className={`absolute top-2 right-2 px-2 py-1 text-xs font-medium rounded-full ${
          isOnline ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'
        }`}
      >
        {isOnline ? 'Online' : 'Offline'}
      </span>

      {/* Zone count summary badge - top left */}
      {hasZones && (
        <div className="absolute top-2 left-2 flex gap-1">
          <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 flex items-center gap-1">
            <Package className="w-3 h-3" />
            {hasCounts ? totalCount : '—'}
          </span>
          {lowStockCount > 0 && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-orange-100 text-orange-700">
              ⚠️ {lowStockCount}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

export default CameraSnapshotThumbnail;
